import { useEffect, useState } from 'react';
import Button from 'src/components/ui/Button';
import { ConfettiIcon } from '@phosphor-icons/react';
import { useRightPanelControl } from 'src/hooks/useRightPanelControl';
import OnboardingContent from './OnboardingContent';
import Step from 'src/components/ui/Step';
import { useLocation } from 'react-router-dom';
import useTenantNavigate from 'src/hooks/useTenantNavigate';
import RightPanelShell from 'src/components/layouts/RightPanelShell';

export default function WritingGuide() {
  const { hideOnboarding, setContent } = useRightPanelControl();
  const navigate = useTenantNavigate();
  const location = useLocation();

  // Local step states
  const [steps, setSteps] = useState<{
    tour: 'incomplete' | 'loading' | 'complete';
    download: 'incomplete' | 'loading' | 'complete';
    upload: 'incomplete' | 'loading' | 'complete';
    edit: 'incomplete' | 'loading' | 'complete';
    docx: 'incomplete' | 'loading' | 'complete';
  }>({
    tour: 'loading',
    download: 'incomplete',
    upload: 'incomplete',
    edit: 'incomplete',
    docx: 'incomplete'
  });

  // Navigate underlying page to /write once when opening Writing Assistant
  useEffect(() => {
    if (!window.location.pathname.includes('/write')) {
      navigate('/write');
    }
    // run only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If writing tour already completed, reflect in first step
  useEffect(() => {
    const completed = localStorage.getItem('innex-writing-tour-completed') === 'true';
    if (completed) {
      setSteps((s) => ({ ...s, tour: 'complete', download: 'loading' }));
    }
  }, []);

  // While tour is running, detect completion and advance states
  useEffect(() => {
    if (steps.tour !== 'loading') return;
    const id = setInterval(() => {
      const completed = localStorage.getItem('innex-writing-tour-completed') === 'true';
      if (completed) {
        setSteps((s) => ({ ...s, tour: 'complete', download: 'loading' }));
        clearInterval(id);
      }
    }, 500);
    return () => clearInterval(id);
  }, [steps.tour]);

  // Auto-complete download step if already downloaded earlier
  useEffect(() => {
    if (steps.download !== 'loading') return;
    const id = setInterval(() => {
      const downloaded = localStorage.getItem('innex-writing-sample-transcript-downloaded') === 'true';
      if (downloaded) {
        setSteps((s) => ({ ...s, download: 'complete', upload: 'loading' }));
        clearInterval(id);
      }
    }, 500);
    return () => clearInterval(id);
  }, [steps.download]);

  // Mark "Upload and Generate" as complete once we navigate to a specific generated document page
  useEffect(() => {
    if (steps.upload !== 'loading') return;
    const isFinalDoc = location.pathname.includes('/write/') && !location.pathname.endsWith('/write');
    if (isFinalDoc) {
      setSteps((prev) => ({ ...prev, upload: 'complete', edit: 'loading' }));
    }
  }, [location.pathname, steps.upload]);

  // While edit step is running, detect when a successful AI edit (new version) has occurred
  useEffect(() => {
    if (steps.edit !== 'loading') return;
    const id = setInterval(() => {
      const edited = localStorage.getItem('innex-write-edited') === 'true';
      if (edited) {
        setSteps((s) => ({ ...s, edit: 'complete', docx: 'loading' }));
        clearInterval(id);
      }
    }, 500);
    return () => clearInterval(id);
  }, [steps.edit]);

  // While docx step is running, detect completion (FinalDocumentPage sets this flag)
  useEffect(() => {
    if (steps.docx !== 'loading') return;
    const id = setInterval(() => {
      const downloaded = localStorage.getItem('innex-write-docx-downloaded') === 'true';
      if (downloaded) {
        setSteps((s) => ({ ...s, docx: 'complete' }));
        clearInterval(id);
      }
    }, 500);
    return () => clearInterval(id);
  }, [steps.docx]);

  const backToOnboarding = () => {
    setContent(<OnboardingContent onComplete={hideOnboarding} />);
  };

  const header = (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        className="text-sm text-secondary-blue hover:underline self-start"
        onClick={backToOnboarding}
      >
        ← Back to all modules
      </button>
      <h2 className="text-lg font-semibold text-content-default">Writing Assistant</h2>
    </div>
  );

  const allComplete =
    steps.tour === 'complete' &&
    steps.download === 'complete' &&
    steps.upload === 'complete' &&
    steps.edit === 'complete' &&
    steps.docx === 'complete';

  const skipStep = () => {
    setSteps((s) => {
      const order = ['tour', 'download', 'upload', 'edit', 'docx'] as const;
      const loadingIdx = order.findIndex((k) => s[k] === 'loading');
      const nextState = { ...s } as typeof s;
      if (loadingIdx >= 0) {
        nextState[order[loadingIdx]] = 'incomplete';
        const relNextIdx = order.slice(loadingIdx + 1).findIndex((k) => s[k] === 'incomplete');
        if (relNextIdx >= 0) {
          const idx = loadingIdx + 1 + relNextIdx;
          nextState[order[idx]] = 'loading';
        }
        return nextState;
      }
      const firstIncompleteIdx = order.findIndex((k) => s[k] === 'incomplete');
      if (firstIncompleteIdx >= 0) {
        nextState[order[firstIncompleteIdx]] = 'loading';
        return nextState;
      }
      return s;
    });
  };

  const footer = allComplete ? (
    <Button
      variant="primary"
      size="md"
      className="w-full"
      onClick={() => {
        try {
          localStorage.setItem('innex-onboarding-module-write-completed', 'true');
        } catch {}
        hideOnboarding();
      }}
    >
      Mark as complete
    </Button>
  ) : (
    <div className="flex flex-col gap-2">
      <Button variant="outline" size="md" className="w-full" onClick={skipStep}>
        Skip Step
      </Button>
      <Button variant="outline" size="md" className="w-full" onClick={() => navigate('/tutorials/writing-page')}>
        Watch Writing Tutorials
      </Button>
    </div>
  );

  return (
    <RightPanelShell header={header} footer={footer}>
      <div className="flex flex-col gap-2">
        {(() => {
          const setActiveStep = (key: 'tour' | 'download' | 'upload' | 'docx') => {
            setSteps((prev) => {
              if (prev[key] === 'loading') return prev;
              const next = { ...prev } as typeof prev;
              if (next.tour === 'loading') next.tour = 'incomplete';
              if (next.download === 'loading') next.download = 'incomplete';
              if (next.upload === 'loading') next.upload = 'incomplete';
              if (next.docx === 'loading') next.docx = 'incomplete';
              next[key] = 'loading';
              return next;
            });
          };

          return (
            <>
              <Step
                title="Complete Tour"
                state={steps.tour}
                onTitleClick={() => {
                  try {
                    localStorage.removeItem('innex-writing-tour-completed');
                  } catch {}
                  setActiveStep('tour');
                }}
              >
                <div className="flex flex-col gap-2">
                  <div className="text-sm text-content-subtle">
                    Start our short guided tour to familiarise yourself with the Writing Assistant.
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      try {
                        window.dispatchEvent(new Event('start-writing-tour'));
                      } catch {}
                    }}
                  >
                    Start tour
                  </Button>
                </div>
              </Step>

              <Step
                title="Download Sample Transcript"
                state={steps.download}
                onTitleClick={() => {
                  try {
                    localStorage.removeItem('innex-writing-sample-transcript-downloaded');
                  } catch {}
                  setActiveStep('download');
                }}
              >
                {steps.download === 'loading' ? (
                  <div className="flex flex-col gap-2">
                    <div className="text-sm text-content-subtle">
                      Download the sample transcript to use in the next step.
                    </div>
                    <Button
                      onClick={async () => {
                        try {
                          try {
                            localStorage.setItem('innex-writing-sample-transcript-downloaded', 'true');
                          } catch {}
                          setSteps((s) => ({ ...s, download: 'complete', upload: 'loading' }));

                          const response = await fetch(
                            'https://innexpublic.blob.core.windows.net/platform-assets/onboarding/Sample_Transcript.docx'
                          );
                          if (!response.ok) throw new Error('Failed to fetch');
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = 'Sample_Transcript.docx';
                          link.style.display = 'none';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(url);
                        } catch (error) {
                          console.error('Download failed, opening in new tab:', error);
                          window.open(
                            'https://innexpublic.blob.core.windows.net/platform-assets/onboarding/Sample_Transcript.docx',
                            '_blank'
                          );
                        }
                      }}
                      icon={undefined}
                      variant="primary"
                      size="sm"
                    >
                      Sample_Transcript.docx
                    </Button>
                  </div>
                ) : undefined}
              </Step>

              <Step
                title="Upload Transcript and Generate"
                state={steps.upload}
                onTitleClick={() => setActiveStep('upload')}
              >
                {steps.upload === 'loading' ? (
                  <div className="text-sm text-content-subtle">
                    1. Click on "Use Template" for Meeting Minutes. <br /> 2. Upload the transcript and any supporting
                    documents. <br /> 3. Click "Proceed" to generate your document.
                  </div>
                ) : undefined}
              </Step>

              <Step title="Ask AI to Edit the Document" state={steps.edit} onTitleClick={() => setActiveStep('upload')}>
                {steps.edit === 'loading' ? (
                  <div className="text-sm text-content-subtle">
                    1. Open the AI Editor and ask it to make a small change. You can use the example below. <br />{' '}
                    <br />
                    <i>Add John Smith to the list of apologies</i>
                  </div>
                ) : undefined}
              </Step>

              <Step
                title="Download the DOCX"
                state={steps.docx}
                isLast={!allComplete}
                onTitleClick={() => setActiveStep('docx')}
              >
                {steps.docx === 'loading' ? (
                  <div className="text-sm text-content-subtle">
                    Click "Download" on the document page to save your minutes as a DOCX. This step will auto-complete
                    when the download starts.
                  </div>
                ) : undefined}
              </Step>

              {allComplete && (
                <Step
                  title="Congrats, you're all set!"
                  state="loading"
                  isLast={true}
                  icon={<ConfettiIcon size={28} className="text-secondary-blue" />}
                >
                  <div className="text-sm text-content-subtle">
                    You're one step closer to getting the most out of CompliMind and saving yourself hours at work!
                  </div>
                </Step>
              )}
            </>
          );
        })()}
      </div>
    </RightPanelShell>
  );
}
